/*
 * Site-wide constants and URL helpers. Kept free of imports so the router, the
 * components and the build-time prerender script can all share it.
 */

export const site = {
  origin: 'https://activelittleminds.com',
  name: 'Active Little Minds',
  legalName: 'Active Little Minds — Child Development Centre',
  locale: 'en-IN',
  // Social sharing image (1200x630), generated by scripts/tools/optimize-images.mjs
  ogImage: {
    path: '/og-image.jpg',
    width: 1200,
    height: 630,
    alt: 'A child hanging upside down and smiling on playground climbing bars.',
  },
  logoPath: '/icon-512.png',
  // Carried over from the old GoDaddy site so the existing Search Console
  // property stays verified after the move.
  googleSiteVerification: 'AMmt9ig1C2dYuZpx0CvwhLLrMIkk8nqw74t3EhgCxeY',
  // Google Analytics 4 measurement ID (injected in public/index.html).
  gaMeasurementId: 'G-39K18ZX0KT',
};

/**
 * Every internal URL ends in a slash. GitHub Pages serves each page as a
 * folder, so /about answers with a 301 to /about/. Linking to and declaring
 * the slash form everywhere avoids that redirect hop for visitors and crawlers.
 */
export const paths = {
  home: '/',
  about: '/about/',
  services: '/services/',
  service: (slug) => `/services/${slug}/`,
  blog: '/blog/',
  post: (slug) => `/blog/${slug}/`,
  contact: '/contact/',
};

/** Normalises any pathname to the canonical trailing-slash form. */
export const withSlash = (pathname) => {
  const clean = (pathname || '/').split(/[?#]/)[0];
  return clean.endsWith('/') ? clean : `${clean}/`;
};

export const absoluteUrl = (path) => `${site.origin}${path}`;

/**
 * Old URLs -> where their content lives now. Two generations are covered:
 *  1. the original GoDaddy site (/about-us, /f/<post>)
 *  2. the first React launch, before slugs were shortened.
 * The build writes a static redirect page for each, and the router also
 * handles them client-side as a safety net.
 */
export const legacyRedirects = {
  // GoDaddy site
  '/about-us': '/about/',
  '/f/autism-spectrum-disorder-asd': '/blog/autism-spectrum-disorder/',
  '/f/top-8-tips-for-early-childhood-development-|-active-little-minds':
    '/blog/early-childhood-development-tips/',
  // First React launch (long slugs)
  '/services/speech-and-language-therapy': '/services/speech-therapy/',
  '/services/occupational-therapy-and-sensory-integration':
    '/services/occupational-therapy/',
  '/services/neurodevelopment-therapy-and-early-intervention':
    '/services/early-intervention/',
  '/services/pediatric-physiotherapy-and-sports-therapy':
    '/services/physiotherapy/',
  '/services/special-education-and-remedial-classes':
    '/services/special-education/',
  '/services/craniosacral-and-oral-placement-therapy':
    '/services/craniosacral-oral-placement/',
  '/services/group-and-social-therapy': '/services/group-therapy/',
  '/blog/autism-spectrum-disorder-asd': '/blog/autism-spectrum-disorder/',
  '/blog/top-8-tips-for-early-childhood-development':
    '/blog/early-childhood-development-tips/',
};

/** Resolve a (possibly URL-encoded) legacy pathname to its new location. */
export const legacyTarget = (pathname) => {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch (e) {
    /* keep the raw value */
  }
  const key = decoded.replace(/\/+$/, '');
  return legacyRedirects[key] || null;
};
